import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { uploadToBucket } from "@/lib/uploads";
import { toast } from "sonner";
import { Plus, ClipboardList, Paperclip, ChevronRight, Save } from "lucide-react";

export const Route = createFileRoute("/teacher/assignments")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  validateSearch: (s: Record<string, unknown>) => ({ open: typeof s.open === "string" ? s.open : undefined }),
  head: () => ({ meta: [{ title: "Assignments — MAKAI" }] }),
  component: AssignmentsPage,
});


function AssignmentsPage() {
  const ctx = useTeacherContext();
  const search = Route.useSearch();
  const [items, setItems] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    if (!ctx.staffId) return;
    const { data } = await supabase
      .from("assignments")
      .select("id,title,instructions,max_score,due_at,assigned_on,status,stream_id,subject_id,stream:streams(name,class:classes(name)),subject:subjects_catalog(name)")
      .eq("teacher_id", ctx.staffId).order("created_at", { ascending: false });
    setItems(data ?? []);
  };

  useEffect(() => { if (!ctx.loading) load(); }, [ctx.loading, ctx.staffId]);

  // Auto-open MarkSheet when ?open=<assignmentId> is in the URL
  useEffect(() => {
    if (!search.open || !items.length) return;
    const target = items.find((a) => a.id === search.open);
    if (target) setSelected(target);
  }, [search.open, items]);


  return (
    <AppShell role="teacher" title="Assignments" subtitle="Set homework, attach materials, then mark and give feedback."
      action={<Button onClick={() => setOpen(true)}><Plus className="mr-1 h-4 w-4" /> New assignment</Button>}>
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          No assignments yet.
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {items.map((a) => (
            <button key={a.id} onClick={() => setSelected(a)} className="rounded-2xl border border-border bg-card p-4 text-left shadow-soft hover:border-primary/40">
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
                <div className="min-w-0">
                  <div className="truncate font-display font-bold">{a.title}</div>
                  <div className="text-xs text-muted-foreground">{a.stream?.class?.name} · {a.stream?.name} · {a.subject?.name}</div>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${a.status === "published" ? "bg-primary-soft text-primary" : "bg-muted text-muted-foreground"}`}>{a.status}</span>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                <span>Due {a.due_at ? new Date(a.due_at).toLocaleString() : "—"}</span>
                <span className="inline-flex items-center gap-1 font-semibold text-primary">Mark <ChevronRight className="h-3.5 w-3.5" /></span>
              </div>
            </button>
          ))}
        </div>
      )}

      <AssignmentDialog open={open} onOpenChange={setOpen} ctx={ctx} onSaved={load} />
      {selected && <MarkSheet assignment={selected} onClose={() => { setSelected(null); load(); }} />}
    </AppShell>
  );
}

function AssignmentDialog({ open, onOpenChange, ctx, onSaved }: any) {
  const [form, setForm] = useState<any>({});
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const sid = ctx.streams[0]?.id ?? "";
      setForm({
        title: "", instructions: "", max_score: 100, due_at: "", status: "published",
        stream_id: sid,
        subject_id: ctx.assignments.filter((a: any) => a.stream.id === sid && a.subject)[0]?.subject?.id ?? "",
      });
      setFiles([]);
    }
  }, [open, ctx]);

  const subjectsForStream = ctx.assignments.filter((a: any) => a.stream.id === form.stream_id && a.subject).map((a: any) => a.subject);

  async function save() {
    if (!form.title || !form.stream_id || !form.subject_id) { toast.error("Title, class and subject required"); return; }
    setSaving(true);
    try {
      const { data, error } = await supabase.from("assignments").insert({
        ...form, school_id: ctx.schoolId, teacher_id: ctx.staffId,
        due_at: form.due_at || null, max_score: Number(form.max_score) || 100,
      }).select("id").single();
      if (error) throw error;
      for (const f of files) {
        const up = await uploadToBucket("teacher-files", f, `assignments/${data.id}`);
        await supabase.from("assignment_attachments").insert({
          assignment_id: data.id, school_id: ctx.schoolId, kind: (up.kind === "document" ? "file" : up.kind), title: up.name,
          url: up.url, storage_path: up.path, size_bytes: up.size,
        });
      }
      toast.success("Assignment created");
      onOpenChange(false); onSaved();
    } catch (e: any) { toast.error(e.message); } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New assignment</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Input placeholder="Title" value={form.title ?? ""} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <div className="grid gap-3 sm:grid-cols-2">
            <select value={form.stream_id ?? ""} onChange={(e) => setForm({ ...form, stream_id: e.target.value, subject_id: "" })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
              {ctx.streams.map((s: any) => <option key={s.id} value={s.id}>{s.class_name} · {s.name}</option>)}
            </select>
            <select value={form.subject_id ?? ""} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
              <option value="">Subject…</option>
              {subjectsForStream.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <Textarea placeholder="Instructions for learners" rows={4} value={form.instructions ?? ""} onChange={(e) => setForm({ ...form, instructions: e.target.value })} />
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">Max score
              <Input type="number" value={form.max_score ?? 100} onChange={(e) => setForm({ ...form, max_score: e.target.value })} />
            </label>
            <label className="text-sm">Due date
              <Input type="datetime-local" value={form.due_at ?? ""} onChange={(e) => setForm({ ...form, due_at: e.target.value })} />
            </label>
          </div>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
            <option value="draft">Draft</option>
            <option value="published">Publish to learners</option>
          </select>
          <div>
            <label className="text-sm font-medium">Attachments</label>
            <input type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files ?? []))} className="mt-1 block w-full text-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarkSheet({ assignment, onClose }: { assignment: any; onClose: () => void }) {
  const [rows, setRows] = useState<any[]>([]);
  const [edits, setEdits] = useState<Record<string, { score: string; feedback: string }>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: learners } = await supabase.from("learners")
        .select("id, first_name, last_name")
        .eq("current_stream_id", assignment.stream_id).eq("status", "active").order("first_name");
      const { data: subs } = await supabase.from("assignment_submissions")
        .select("id, learner_id, status, score, feedback, submitted_at")
        .eq("assignment_id", assignment.id);
      const byL = new Map((subs ?? []).map((s: any) => [s.learner_id, s]));
      const merged = (learners ?? []).map((l: any) => ({ ...l, sub: byL.get(l.id) ?? null }));
      setRows(merged);
      const e: any = {};
      for (const m of merged) e[m.id] = { score: m.sub?.score ?? "", feedback: m.sub?.feedback ?? "" };
      setEdits(e);
    })();
  }, [assignment.id, assignment.stream_id]);

  async function saveAll() {
    setSaving(true);
    try {
      const ups = rows.map((r) => ({
        assignment_id: assignment.id, school_id: assignment.school_id ?? assignment.stream?.school_id, learner_id: r.id,
        score: edits[r.id]?.score === "" ? null : Number(edits[r.id]?.score),
        feedback: edits[r.id]?.feedback || null,
        status: edits[r.id]?.score !== "" ? "graded" : (r.sub?.status ?? "pending"),
        graded_at: edits[r.id]?.score !== "" ? new Date().toISOString() : null,
      }));
      // Pull school_id from assignment row
      const { data: a } = await supabase.from("assignments").select("school_id").eq("id", assignment.id).single();
      for (const u of ups) (u as any).school_id = a?.school_id;
      const { error } = await supabase.from("assignment_submissions").upsert(ups, { onConflict: "assignment_id,learner_id" } as any);
      if (error) throw error;
      toast.success("Marks saved");
    } catch (e: any) { toast.error(e.message); } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{assignment.title}</DialogTitle>
          <div className="text-xs text-muted-foreground">{assignment.stream?.class?.name} · {assignment.stream?.name} · max {assignment.max_score}</div>
        </DialogHeader>
        <div className="overflow-hidden rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="px-3 py-2">Learner</th><th className="px-3 py-2 w-20">Score</th><th className="px-3 py-2">Feedback</th><th className="px-3 py-2">Status</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2 font-medium">{r.first_name} {r.last_name}</td>
                  <td className="px-3 py-2">
                    <Input value={edits[r.id]?.score ?? ""} onChange={(e) => setEdits({ ...edits, [r.id]: { ...edits[r.id], score: e.target.value } })} className="h-8" />
                  </td>
                  <td className="px-3 py-2">
                    <Input value={edits[r.id]?.feedback ?? ""} onChange={(e) => setEdits({ ...edits, [r.id]: { ...edits[r.id], feedback: e.target.value } })} className="h-8" />
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{r.sub?.status ?? "pending"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={saveAll} disabled={saving}><Save className="mr-1 h-4 w-4" /> {saving ? "Saving…" : "Save marks"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
